import { TRAINING_GATE_FACTIONS } from "@frontier/content/training";
import { canDockAt, nearbyStation } from "@frontier/content/stations";
import { isActiveContract } from "@frontier/content/contract-orders";
import type { MissionView } from "@frontier/content/contracts";
import { REGIONS } from "@frontier/content/contracts";
import { SURVEY_RADIUS } from "@frontier/content/progression";
import type { PublicEventsView } from "@frontier/content/events";
import { regionRisk } from "@frontier/content/risk";
import type { ShipSnapshot, WorldSnapshot } from "@frontier/protocol/contracts";
import { eventWorkAvailable } from "./event-navigation";

type Interaction =
  | {
      type: "dock" | "survey" | "eventInteract" | "pickup" | "jump";
      targetId?: string;
      riskAcknowledged?: boolean;
    };

export function surveyAvailable(
  mission: MissionView | null,
  pilot: Pick<ShipSnapshot, "x" | "y" | "dead" | "docked" | "pending">,
) {
  const region = REGIONS.find((item) => item.id === mission?.sectorId);
  const objective = mission?.objective;
  if (
    !region ||
    !mission ||
    !isActiveContract(mission) ||
    mission.contractId !== "salvage" ||
    objective?.kind !== "survey" ||
    !objective.targetId ||
    !mission.terms.requiredSurveySites.some((id) => id === objective.targetId) ||
    pilot.dead ||
    pilot.docked ||
    pilot.pending ||
    pilot.x < region.x ||
    pilot.x >= region.x + region.width ||
    pilot.y < region.y ||
    pilot.y >= region.y + region.height ||
    Math.hypot(pilot.x - objective.x, pilot.y - objective.y) > SURVEY_RADIUS
  )
    return undefined;
  return objective;
}

export function availableSurvey(
  missions: readonly MissionView[],
  pilot: Pick<ShipSnapshot, "x" | "y" | "dead" | "docked" | "pending">,
) {
  for (const mission of missions) {
    const objective = surveyAvailable(mission, pilot);
    if (objective) return objective;
  }
  return undefined;
}

export function flightInteraction(
  world: WorldSnapshot,
  pilot: ShipSnapshot,
  missions: readonly MissionView[],
  events: PublicEventsView,
): Interaction | null {
  if (pilot.docked || pilot.dead || pilot.pending) return null;
  const station = nearbyStation(pilot);
  if (station && canDockAt(station, pilot.factionId))
    return { type: "dock" };
  const survey = availableSurvey(missions, pilot);
  if (survey?.targetId) return { type: "survey", targetId: survey.targetId };
  const work = eventWorkAvailable(world, pilot, events);
  if (work) return work.workBlocked ? null : { type: "eventInteract", targetId: work.id };
  const loot = world.loot
    .filter((item) => !item.pending && !item.claimedBy.includes(pilot.id))
    .sort(
      (a, b) => Math.hypot(a.x - pilot.x, a.y - pilot.y) - Math.hypot(b.x - pilot.x, b.y - pilot.y),
    )[0];
  if (loot && Math.hypot(loot.x - pilot.x, loot.y - pilot.y) <= 125)
    return { type: "pickup", targetId: loot.id };
  const gate = world.gates.find(
    (item) => Math.hypot(item.x - pilot.x, item.y - pilot.y) <= item.radius,
  );
  if (!gate) return null;
  if (TRAINING_GATE_FACTIONS[gate.id]) return { type: "jump", targetId: gate.id };
  return {
    type: "jump",
    ...(regionRisk(gate.toSectorId) === "open-pvp" ? { riskAcknowledged: true } : {}),
    targetId: gate.id,
  };
}
